/* eslint-disable */
import { useContext, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { VerifyLoginRequest } from "../../models/request/verifyLoginRequest";
import { updateTfaProvider, verifyTfaCode } from "../../store/authSlice";
import { RootState, useAppDispatch, useAppSelector } from "../../store/store";
import { LocalizationContext } from "../../App";
import { AuthLayout } from "../../layout/AuthLayout";

const TfaVerify = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const l10n = useContext(LocalizationContext);
  const schema = yup.object().shape({
    code: yup.string().required(l10n.error.codeRequired),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema)
  });
  const isUserLogged = useAppSelector(
    (state: RootState) => state.auth.isUserLogged
  );
  const md5Key = useAppSelector((state: any) => state.auth.md5Key);
  const qrCodeImage = useAppSelector(
    (state: RootState) => state.auth.qrCodeImage
  );
  const manualSetupKey = useAppSelector(
    (state: RootState) => state.auth.manualSetupKey
  );
  const errorCode = useAppSelector((state: RootState) => state.auth.error);

  useEffect(() => {
    if (isUserLogged) {
      navigate("/");
    }

    if (md5Key === null || errorCode === "EXPIREDLOGINSESSION") {
      navigate("/login");
    }
  }, [isUserLogged, md5Key, manualSetupKey, qrCodeImage, navigate, errorCode]);

  const verifyCode = (data: any) => {
    const verifyRequest = new VerifyLoginRequest();
    verifyRequest.md5HashCode = md5Key;
    verifyRequest.codeToVerify = (typeof data.code === "string" || data.code instanceof String) ? data.code?.trim() : data.code;
    dispatch(verifyTfaCode({ verifyRequest: verifyRequest }));
  };

  const tfaVerified = () => {
    dispatch(updateTfaProvider({ md5HashCode: md5Key }));
  };

  return (
    <AuthLayout>
      <div className="h-full flex-1 flex flex-col landscape:pb-8">
        {!qrCodeImage ? (
          <form
            className="h-full flex-1 flex flex-col justify-between"
            onSubmit={handleSubmit(verifyCode)}
          >
            <div className="flex-1 flex flex-col justify-center">
              <h2>{l10n.auth.enterCodeYouReceived}</h2>
              <div className="form-group mb-0">
                <label hidden htmlFor="code">
                  {l10n.label.yourCode}
                </label>
                <input
                  autoFocus
                  className="form-control"
                  id="code"
                  placeholder={l10n.label.yourCode}
                  tabIndex={0}
                  type="text"
                  {...register("code")}
                />
                <>
                  {errors.code && <p className="invalid-feedback">{errors.code.message}</p>}
                  {errorCode && errorCode === "INVALIDCODE" && (
                    <p className="invalid-feedback">{l10n.auth.invalidTfaCode}</p>
                  )}
                  {errorCode && errorCode === "ACCOUNTLOCKED" && (
                    <p className="invalid-feedback">{l10n.auth.accountLocked}</p>
                  )}
                </>
              </div>
            </div>
            <div className="flex-1 flex flex-col justify-end">
              <button className="btn btn-primary" type="submit">{l10n.btn.confirm}</button>
            </div>
          </form>
        ) : (
          <div className="form-group flex-grow mb-0 flex flex-col gap-2 justify-between">
            <div className="w-full flex flex-col">
              <h2>{l10n.auth.toUseAuthAppFollowSteps}</h2>
              <ol className="pl-6 list-outside list-decimal mb-3">
                <li className="mb-3">
                  <span>
                    {l10n.auth.downloadMicrosoftAuthenticator}&nbsp;
                    <a
                      className="underline"
                      href="https://go.microsoft.com/fwlink/?Linkid=825072"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Android
                    </a>
                    &nbsp;{l10n.common.and}&nbsp;
                    <a
                      className="underline"
                      href="https://go.microsoft.com/fwlink/?Linkid=825073"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      iOS
                    </a>
                    ,&nbsp;{l10n.auth.orGoogleAuthenticator}&nbsp;
                    <a
                      className="underline"
                      href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&amp;hl=en"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      Android
                    </a>
                    &nbsp;{l10n.common.and}&nbsp;
                    <a
                      className="underline"
                      href="https://itunes.apple.com/us/app/google-authenticator/id388497605?mt=8"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      iOS
                    </a>
                    .
                  </span>
                </li>
                <li>
                  <span className="inline-block">
                    {l10n.auth.scanQrCodeBelow}
                  </span>
                  <div
                    className="relative flex-grow -ml-6 my-6 h-min flex justify-center"
                    id="qrCode"
                  >
                    <img
                      alt="QR Code"
                      className="w-40 h-40 outline outline-1 outline-gray-200 outline-offset-8 rounded-md"
                      src={qrCodeImage}
                    />
                  </div>
                  <span className="inline-block">
                    {l10n.auth.orEnterThisKey}:
                  </span>
                  <div className="relative -ml-6 my-4 h-min bg-white p-2 outline outline-[1px] outline-gray-200 rounded-md font-medium font-mono tracking-widest break-all text-wrap">
                    {manualSetupKey}
                  </div>
                  <span>{l10n.auth.intoYourTfaApp}</span>
                </li>
              </ol>
            </div>
            <button className="btn btn-primary" onClick={tfaVerified}>
              {!qrCodeImage ? l10n.btn.confirm : l10n.common.next}
            </button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default TfaVerify;
